import * as React from "react";
import { LineChart } from "@mui/x-charts";
import { getPatientsChart } from "../../../api/dashboardService";
import ShowChartIcon from "@mui/icons-material/ShowChart";

const xLabels = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function getLast7Days() {
  const daysArray = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const day = new Date(today);
    day.setDate(today.getDate() - i);
    daysArray.push(day.toISOString().split("T")[0]);
  }
  return daysArray;
}

const UniquePatientChart = React.memo(() => {
  const [select, setselect] = React.useState("month");
  const [isDark, setIsDark] = React.useState(
    () => (document.documentElement.getAttribute("data-theme") || localStorage.getItem("theme")) === "dark"
  );

  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute("data-theme") === "dark");
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);
  const [data, setdata] = React.useState<any>({
    series: [],
    xaxis: [],
  });
  const [count, setCount] = React.useState({
    patient: [],
    appointment: [],
    patientWeek: [],
    appointmentWeek: [],
    week: [],
  });

  React.useEffect(() => {
    getPatientsChart()
      .then((data) => {
        setCount((prevCount: any) => {
          const newPatientData =
            data?.count?.map((item) => item.count) || [];
          const newPatientDataWeek =
            data?.county?.map((item) => item.count) || [];
          const newAppointmentData =
            data?.appointment?.map((item) => item.count) || [];
          const newAppointmentDataWeek =
            data?.appointments?.map((item) => item.count) || [];
          const newWeek =
            data?.appointments?.map((item) => item.date) || [];

          if (
            JSON.stringify(prevCount.patient) !== JSON.stringify(newPatientData) ||
            JSON.stringify(prevCount.appointment) !== JSON.stringify(newAppointmentData)
          ) {
            return {
              patient: newPatientData,
              appointment: newAppointmentData,
              patientWeek: newPatientDataWeek,
              appointmentWeek: newAppointmentDataWeek,
              week: newWeek,
            };
          }
          return prevCount;
        });
      })
      .catch((error) => {
        console.error(error.response);
      });
  }, []);

  const seriesData = React.useMemo(
    () => [
      {
        data: count.patient,
        label: "Patient",
        area: true,
        stack: "total",
        showMark: false,
        color: "#818cf8",
      },
      {
        data: count.appointment,
        label: "Appointment",
        area: true,
        stack: "total",
        showMark: false,
        color: "#6366f1",
      },
    ],
    [count]
  );

  const seriesData1 = React.useMemo(
    () => [
      {
        data: count.patientWeek,
        label: "Patient",
        area: true,
        stack: "total",
        showMark: false,
        color: "#818cf8",
      },
      {
        data: count.appointmentWeek,
        label: "Appointment",
        area: true,
        stack: "total",
        showMark: false,
        color: "#6366f1",
      },
    ],
    [count]
  );

  const handleChange = (value: string) => {
    setselect(value);
    if (value === "month") {
      setdata({ series: seriesData, xaxis: xLabels });
    } else {
      setdata({ series: seriesData1, xaxis: getLast7Days() });
    }
  };

  return (
    <div className="rounded-2xl shadow-lg border overflow-hidden transition-all duration-300 bg-white border-slate-100 shadow-slate-200/50 dark:bg-slate-800 dark:border-slate-700 dark:shadow-slate-900/50">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-primaryLight rounded-xl flex items-center justify-center">
            <ShowChartIcon className="text-white" sx={{ fontSize: 20 }} />
          </div>
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Patient & Appointment Trends</h3>
        </div>
        <div className="flex gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
          <button
            onClick={() => handleChange("month")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              select === "month"
                ? "bg-white text-indigo-600 shadow-sm dark:bg-slate-600 dark:text-indigo-300"
                : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            }`}
          >
            Month
          </button>
          <button
            onClick={() => handleChange("week")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
              select === "week"
                ? "bg-white text-indigo-600 shadow-sm dark:bg-slate-600 dark:text-indigo-300"
                : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            }`}
          >
            Week
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="p-4">
        <LineChart
          height={350}
          series={data?.series?.length > 0 ? data?.series : seriesData}
          xAxis={[
            {
              scaleType: "point",
              data: data?.xaxis?.length > 0 ? data?.xaxis : xLabels,
              tickSize: 10,
            },
          ]}
          sx={{
            width: "100%",
            "& .MuiChartsAxis-tickLabel": {
              fill: isDark ? "#94a3b8" : "#64748b",
              fontSize: "12px",
            },
            "& .MuiChartsLegend-label": {
              fill: isDark ? "#cbd5e1" : "#475569",
              fontSize: "13px",
            },
          }}
        />
      </div>
    </div>
  );
});

export default UniquePatientChart;
